import type { CSSProperties } from "react";

/**
 * ============================================================================
 *  splashStyles.ts — L'habillage de l'écran d'accueil « Écrin »
 * ----------------------------------------------------------------------------
 *  Les valeurs viennent du handoff de design (`sources/design/…/README.md`) :
 *  couleurs, tailles et durées sont arrêtées. Elles sont réunies ici pour que
 *  `SplashScreen.tsx` reste lisible d'une traite.
 *
 *  Les tailles sont FLUIDES (`clamp()`, unités `cqw`/`cqh`) : une seule mise en
 *  page couvre le téléphone en portrait (390×844, prioritaire) et le bureau
 *  16:9. La racine est un conteneur (`containerType: size`), et c'est son
 *  cadre — pas la fenêtre — qui sert d'unité.
 * ==========================================================================*/

export const COULEURS = {
  fond: "#101A17",
  creme: "#F6EEE3",
  vert: "#22382F",
  sauge: "#B9CDB4",
  texteRepos: "#F1E9DC",
  texteSousTitre: "#E6DCCB",
} as const;

/** Les trois états d'un item, hors désactivé (qui n'a pas de survol). */
export type EtatItem = "repos" | "actif" | "choisi" | "desactive";

export const POLICE_TITRE_SPLASH = "var(--police-splash-titre)";
export const POLICE_UI_SPLASH = "var(--police-splash-ui)";

export const racine: CSSProperties = {
  position: "absolute",
  inset: 0,
  // Au-dessus de tout ce que la coquille pose par-dessus le panorama : l'accueil
  // est un écran, pas un panneau.
  zIndex: 20,
  overflow: "hidden",
  containerType: "size",
  color: COULEURS.creme,
  fontFamily: POLICE_UI_SPLASH,
  pointerEvents: "auto",
};

const plein: CSSProperties = { position: "absolute", inset: 0 };

/**
 * Fond uni du départ : levé en fondu, il révèle le panorama déjà assombri. À la sortie vers un parcours
 * guidé il REVIENT (`rapide`) : l’écran passe au noir plutôt qu’à la scène, et le guide s’y enchaîne.
 */
export const voile = (leve: boolean, rapide = false, reduit = false): CSSProperties => ({
  ...plein,
  background: COULEURS.fond,
  opacity: leve ? 0 : 1,
  transition: `opacity ${rapide ? (reduit ? 0.2 : 0.7) : 1.6}s ease`,
  pointerEvents: "none",
});

/** Dégradé sombre et flou : le panorama du moteur reste dessous, vivant. */
export const teinte = (visible: boolean): CSSProperties => ({
  ...plein,
  background:
    "radial-gradient(ellipse 70% 65% at 50% 46%, rgba(20,34,29,.62), rgba(11,19,16,.86))",
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
  opacity: visible ? 1 : 0,
  transition: "opacity 1.6s ease",
  pointerEvents: "none",
});

export const couche = (sortie: boolean): CSSProperties => ({
  ...plein,
  display: "flex",
  flexDirection: "column",
  opacity: sortie ? 0 : 1,
  transition: "opacity .9s ease",
});

export const barre = (visible: boolean, delai: string): CSSProperties => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  padding: "clamp(10px,1.6cqw,24px) clamp(14px,2.4cqw,36px)",
  opacity: visible ? 1 : 0,
  transition: `opacity 1s ease ${delai}`,
});

export const son: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 11.5,
  letterSpacing: ".14em",
  textTransform: "uppercase",
  color: "#E3D9C8",
};

export const groupeLangues: CSSProperties = { display: "flex", alignItems: "center" };

export const boutonLangue = (active: boolean): CSSProperties => ({
  appearance: "none",
  background: "none",
  border: "none",
  minWidth: 44,
  minHeight: 44,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 4,
  color: active ? "#FFFFFF" : "#CFC4B2",
  font: "inherit",
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: ".14em",
  cursor: "pointer",
});

export const traitLangue = (active: boolean): CSSProperties => ({
  width: 14,
  height: 1,
  background: "currentColor",
  opacity: active ? 1 : 0,
});

export const separateurLangue: CSSProperties = {
  width: 1,
  height: 12,
  background: "rgba(246,238,227,.35)",
};

export const principal: CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 22px",
};

const ENTREE = "cubic-bezier(.2,.7,.2,1)";

export const logo = (visible: boolean, reduit: boolean): CSSProperties => ({
  // Une fois et demie la taille du handoff (200–270 px) : c'est la marque du
  // lieu, elle doit porter l'écran. Seul enfant autorisé à rétrécir : sur un
  // cadre bas (bureau 16:9 de 720 px) c'est lui qui cède, pas le menu.
  flexShrink: 1,
  minHeight: 0,
  objectFit: "contain",
  width: "clamp(300px, max(min(81cqw,39cqh),25.5cqw), 405px)",
  height: "auto",
  opacity: visible ? 1 : 0,
  transform: visible || reduit ? "none" : "translateY(12px) scale(.98)",
  transition: `opacity 1.2s ease, transform 1.4s ${ENTREE}`,
});

export const titre = (visible: boolean, reduit: boolean): CSSProperties => ({
  flexShrink: 0,
  margin: "clamp(12px,2cqh,24px) 0 0",
  fontFamily: POLICE_TITRE_SPLASH,
  fontWeight: 400,
  fontSize: "clamp(30px,3.3cqw,46px)",
  lineHeight: 1.12,
  letterSpacing: ".02em",
  textAlign: "center",
  textWrap: "balance",
  opacity: visible ? 1 : 0,
  transform: visible || reduit ? "none" : "translateY(10px)",
  transition: `opacity 1.2s ease, transform 1.4s ${ENTREE}`,
});

export const filet = (visible: boolean): CSSProperties => ({
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  gap: 10,
  margin: "clamp(10px,2.2cqh,22px) 0",
  opacity: visible ? 1 : 0,
  transition: "opacity 1.2s ease .3s",
});

export const traitFilet: CSSProperties = { width: 36, height: 1, background: "rgba(246,238,227,.5)" };

export const losangleFilet: CSSProperties = {
  width: 5,
  height: 5,
  transform: "rotate(45deg)",
  background: COULEURS.sauge,
};

export const menu: CSSProperties = {
  flexShrink: 0,
  display: "flex",
  flexDirection: "column",
  // Aéré : 4 px du handoff collait les items entre eux, le sous-titre de l'item
  // actif compris.
  gap: 12,
  width: "100%",
  maxWidth: 500,
};

export const ligneItem = (visible: boolean, reduit: boolean, rang: number): CSSProperties => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  flexShrink: 0,
  opacity: visible ? 1 : 0,
  transform: visible || reduit ? "none" : "translateY(14px)",
  transition: `opacity ${reduit ? 0.4 : 0.8}s ease ${rang * (reduit ? 0.06 : 0.14)}s, transform .8s ${ENTREE} ${rang * 0.14}s`,
  pointerEvents: visible ? "auto" : "none",
});

const ASPECT: Record<EtatItem, { color: string; background: string; borderColor: string; marque: number }> = {
  repos: {
    color: COULEURS.texteRepos,
    background: "rgba(246,238,227,0)",
    borderColor: "rgba(246,238,227,0)",
    marque: 0,
  },
  actif: {
    color: "#FFFFFF",
    background: "rgba(246,238,227,.12)",
    borderColor: "rgba(246,238,227,.75)",
    marque: 1,
  },
  choisi: { color: COULEURS.vert, background: COULEURS.creme, borderColor: COULEURS.creme, marque: 1 },
  desactive: {
    color: "rgba(241,233,220,.42)",
    background: "rgba(246,238,227,0)",
    borderColor: "rgba(246,238,227,.12)",
    marque: 0,
  },
};

export const marqueItem = (etat: EtatItem): CSSProperties => ({
  width: 6,
  height: 6,
  transform: "rotate(45deg)",
  background: "currentColor",
  opacity: ASPECT[etat].marque,
  transition: "opacity .25s",
  flex: "none",
});

export const boutonItem = (etat: EtatItem): CSSProperties => {
  const { color, background, borderColor } = ASPECT[etat];
  return {
    appearance: "none",
    outline: "none",
    width: "100%",
    minHeight: 48,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 14,
    padding: "12px 18px",
    color,
    background,
    border: `1px solid ${borderColor}`,
    borderRadius: 2,
    font: "inherit",
    fontSize: "clamp(16px,1.3cqw,18px)",
    fontWeight: 500,
    letterSpacing: ".01em",
    lineHeight: 1.3,
    textAlign: "center",
    cursor: etat === "desactive" ? "not-allowed" : "pointer",
    transition: "background .25s ease, color .25s ease, border-color .25s ease",
  };
};

export const libelleItem: CSSProperties = { textWrap: "balance" };

export const sousTitre = (visible: boolean): CSSProperties => ({
  overflow: "hidden",
  maxHeight: visible ? 60 : 0,
  opacity: visible ? 1 : 0,
  transition: "max-height .35s ease, opacity .35s ease",
  fontSize: 13.5,
  lineHeight: 1.4,
  color: COULEURS.texteSousTitre,
  textAlign: "center",
  padding: "0 12px",
});

export const pied: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 6,
  padding: "14px 20px clamp(16px,2.4cqh,24px)",
};

export const etatChargement: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 11,
  letterSpacing: ".12em",
  textTransform: "uppercase",
  color: "#D9CEBB",
};

export const pointChargement = (anime: boolean): CSSProperties => ({
  width: 5,
  height: 5,
  borderRadius: "50%",
  background: COULEURS.sauge,
  animation: anime ? "splash-pulse 1.6s ease-in-out infinite" : "none",
});

export const signature: CSSProperties = { fontSize: 10.5, letterSpacing: ".08em", color: "#CFC4B2" };

export const pisteProgression = (visible: boolean): CSSProperties => ({
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  height: 2,
  background: "rgba(246,238,227,.14)",
  opacity: visible ? 1 : 0,
  transition: "opacity 1s ease .8s",
});

export const remplissage = (pourcent: number): CSSProperties => ({
  height: "100%",
  width: `${pourcent}%`,
  background: "#E9DFCF",
  transition: "width .25s linear",
});

export const pastilleEntree = (visible: boolean): CSSProperties => ({
  position: "absolute",
  left: 0,
  right: 0,
  bottom: "12%",
  display: "flex",
  justifyContent: "center",
  pointerEvents: "none",
  opacity: visible ? 1 : 0,
  transition: "opacity .8s ease 1.2s",
});

export const pastilleContenu: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 18px",
  background: "rgba(16,26,23,.7)",
  borderRadius: 2,
  fontSize: 12,
  letterSpacing: ".14em",
  textTransform: "uppercase",
  color: COULEURS.creme,
};

/* --- crédits ------------------------------------------------------------- */

export const creditsFond: CSSProperties = {
  ...plein,
  zIndex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  background: "rgba(11,19,16,.78)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
};

